<template>
  <div class="select-race-section">
    <div
      ref="selectRaceSectionListWrapper"
      class="select-race-section__list-wrapper"
      @scroll="checkCarouselEnabled"
    >
      <div class="select-race-section__list">
        <race-list-item-parts
          class="select-race-section__list__item"
          v-for="(championship, index) in championships"
          :class="`select-race-section__list__item-${index}`"
          :key="championship.id"
          :current="championship.id === currentChampionshipId"
          :round="championship.round"
          :courseImg="championship.courseImg"
          :venue="championship.circuitName[$i18n.locale] || undefined"
          :raceDate="championship.raceDateForRaceCard"
          :raceImg="championshipLogoPath(championship)"
          @push="push(championship)"
        />
      </div>
    </div>

    <button
      v-if="enabledRaceListCarousel"
      class="select-race-section__carousel select-race-section__carousel-prev"
      :class="{ 'select-race-section__carousel--active': enabledCarouselPrev }"
      @click="onclickCarousel('prev')"
    >
      <img :src="require('@/assets/img/icon/icon_chevron_left_24.svg')" alt="<" />
    </button>

    <button
      v-if="enabledRaceListCarousel"
      class="select-race-section__carousel select-race-section__carousel-next"
      :class="{ 'select-race-section__carousel--active': enabledCarouselNext }"
      @click="onclickCarousel('next')"
    >
      <img :src="require('@/assets/img/icon/icon_chevron_right_24.svg')" alt=">" />
    </button>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref } from '@vue/composition-api'
import RaceListItemParts from '@/components/RaceListPage/RaceListPane/parts/RaceListItemParts.vue'
import ChampionshipDocument from '@/store/stores/collectionModule/documents/championship/ChampionshipDocument'
import CloudFrontUtil from '@/util/aws/CloudFrontUtil'

/**
 * レース一覧画面: レース選択 コンポーネント
 */
export default defineComponent({
  name: 'RaceListSection',
  components: {
    RaceListItemParts,
  },
  props: {
    championships: {
      type: Array as PropType<Array<ChampionshipDocument>>,
      required: true,
      default: () => [],
    },
    currentChampionshipId: {
      type: String,
      required: true,
      default: '',
    },
    enabledRaceListCarousel: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const enabledCarouselPrev = ref<boolean>(false)
    const enabledCarouselNext = ref<boolean>(false)

    return {
      enabledCarouselPrev,
      enabledCarouselNext,
    }
  },
  mounted() {
    // 読込時に表示判定
    setTimeout(() => this.checkCarouselEnabled(), 500)

    // リサイズ時に表示判定
    window.addEventListener('resize', this.checkCarouselEnabled)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkCarouselEnabled)
  },
  watch: {
    /**
     * レース選択画面で、指定年を変更した際にも、ページャー有効化判定を行うために監視する
     */
    championships(value) {
      if (value.length > 0) {
        this.$nextTick(() => {
          this.checkCarouselEnabled()
        })
      }
    },
  },
  methods: {
    championshipLogoPath(championship: ChampionshipDocument) {
      return CloudFrontUtil.getSignedUrl(championship.championshipLogo)
    },
    push(championship: ChampionshipDocument) {
      /**
       * ボタンが押下されたことを通知する
       * @event change
       * @type ChampionshipDocument
       */
      this.$emit('change', championship)
    },
    /**
     * カルーセルクリックアクション
     * @param type
     */
    onclickCarousel(type: 'prev' | 'next'): void {
      // スクロールターゲット
      const raceLinkList = this.$refs.selectRaceSectionListWrapper as HTMLElement | null
      if (!raceLinkList) {
        return
      }

      // スクロール量取得
      const firstItem = document.querySelector(
        '.select-race-section__list__item-0',
      ) as HTMLElement | null
      if (!firstItem) {
        return
      }
      let scrollValue = -firstItem.clientWidth - 8

      // nextの場合値をマイナス値へ
      scrollValue *= type === 'next' ? -1 : 1

      // スクロール実行
      raceLinkList.scroll({
        left: raceLinkList.scrollLeft + scrollValue,
        behavior: 'smooth',
      })
    },
    /**
     * ページャー有効化判定
     */
    checkCarouselEnabled() {
      const LIST_PADDING = 50
      const raceLinkList = this.$refs.selectRaceSectionListWrapper as HTMLElement | null
      if (!raceLinkList) {
        this.enabledCarouselPrev = false
        this.enabledCarouselNext = false
        return
      }

      this.enabledCarouselPrev = raceLinkList.scrollLeft > LIST_PADDING
      this.enabledCarouselNext =
        raceLinkList.scrollLeft < raceLinkList.scrollWidth - raceLinkList.clientWidth - LIST_PADDING
    },
  },
})
</script>

<style lang="scss" scoped>
.select-race-section {
  width: 100%;
  margin-bottom: 16px;
  transition: all ease 0.2s;

  &__list {
    display: flex;
    flex-flow: row nowrap;
    align-content: flex-start;
    justify-items: flex-start;

    &::after {
      display: block;
      content: '';
      min-width: 8px;
      height: 1px;
    }

    &-wrapper {
      width: 100%;
      overflow-x: auto;

      &::-webkit-scrollbar {
        display: none;
        -webkit-appearance: none;
      }
    }

    &__item {
      margin-left: 8px;
    }
  }

  &__carousel {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    position: absolute;
    bottom: 60px;
    width: 50px;
    height: 50px;
    background: $white;
    border: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border-radius: 50%;
    z-index: 4;
    transition: opacity ease 0.2s;
    opacity: 0;
    pointer-events: none;
    cursor: pointer;

    img {
      pointer-events: none;
    }

    &-prev {
      left: 10px;
    }

    &-next {
      right: 10px;
    }

    &--active {
      opacity: 1;
      pointer-events: auto;
    }
  }
}

@media screen and (max-height: $responsiveMinHeight) {
  .select-race-section {
    margin-bottom: 12px;
  }
}
</style>
